import { COMMENTS_MAX, escapeRich, parseRich, type Run } from '@quickreport/domain';
import clsx from 'clsx';
import {
  Bold,
  Heading,
  Italic,
  List,
  ListOrdered,
  RemoveFormatting,
  Underline,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A small formatted-text editor for a report's Comments: bold, italic, underline, a heading, bullet
 * and numbered lists. The box can be dragged taller or shorter. What is typed is saved as plain
 * text with light markers (see richtext.ts), and pasted text comes in as plain text, so nothing
 * from another program (styles, scripts, images) can get into a report.
 */

// ---- text with markers -> the editor's elements (built as elements, never from HTML text) ------

function runNodes(runs: Run[]): Node[] {
  return runs.flatMap((r) => {
    const parts = r.s.split('\n');
    const nodes: Node[] = [];
    parts.forEach((part, i) => {
      if (i > 0) nodes.push(document.createElement('br'));
      if (!part) return;
      let n: Node = document.createTextNode(part);
      if (r.u) n = wrap('u', n);
      if (r.i) n = wrap('i', n);
      if (r.b) n = wrap('b', n);
      nodes.push(n);
    });
    return nodes;
  });
}
function wrap(tag: string, child: Node): HTMLElement {
  const el = document.createElement(tag);
  el.appendChild(child);
  return el;
}

function fill(root: HTMLElement, md: string) {
  root.replaceChildren();
  for (const b of parseRich(md)) {
    if (b.t === 'p' || b.t === 'h') {
      const el = document.createElement(b.t === 'h' ? 'h4' : 'p');
      for (const n of runNodes(b.runs)) el.appendChild(n);
      root.appendChild(el);
    } else {
      const list = document.createElement(b.t);
      for (const item of b.items) {
        const li = document.createElement('li');
        for (const n of runNodes(item)) li.appendChild(n);
        list.appendChild(li);
      }
      root.appendChild(list);
    }
  }
}

// ---- the editor's elements -> text with markers ------------------------------------------------

function inline(node: Node, out: string[], st: { b: boolean; i: boolean; u: boolean }) {
  if (node.nodeType === Node.TEXT_NODE) {
    const t = (node.textContent ?? '').replace(/ /g, ' ');
    if (!t) return;
    let s = escapeRich(t);
    // Spaces alone are never wrapped in markers.
    if (t.trim()) {
      // Spaces at the edges stay outside the markers, so "**bold **" never happens.
      const lead = /^\s*/.exec(s)?.[0] ?? '';
      const trail = /\s*$/.exec(s)?.[0] ?? '';
      let core = s.slice(lead.length, s.length - trail.length);
      if (st.u) core = `__${core}__`;
      if (st.i) core = `*${core}*`;
      if (st.b) core = `**${core}**`;
      s = lead + core + trail;
    }
    out.push(s);
    return;
  }
  if (!(node instanceof HTMLElement)) return;
  const tag = node.tagName;
  if (tag === 'BR') {
    out.push('\n');
    return;
  }
  const css = node.style;
  const next = {
    b:
      st.b ||
      tag === 'B' ||
      tag === 'STRONG' ||
      css.fontWeight === 'bold' ||
      Number(css.fontWeight) >= 600,
    i: st.i || tag === 'I' || tag === 'EM' || css.fontStyle === 'italic',
    u:
      st.u ||
      tag === 'U' ||
      css.textDecorationLine.includes('underline') ||
      css.textDecoration.includes('underline'),
  };
  for (const c of node.childNodes) inline(c, out, next);
}

function inlineOf(el: Node): string {
  const out: string[] = [];
  for (const c of el.childNodes) inline(c, out, { b: false, i: false, u: false });
  return out.join('');
}

const BLOCK = /^(UL|OL|P|DIV|H[1-6])$/;

export function toMarkers(root: HTMLElement): string {
  const lines: string[] = [];
  // Words that sit directly in the box or in a div (not inside a paragraph or list) are gathered
  // into one paragraph, so a bold word followed by plain words stays one line.
  const walk = (nodes: ChildNode[]) => {
    let buf: Node[] = [];
    const flush = () => {
      const out: string[] = [];
      for (const n of buf) inline(n, out, { b: false, i: false, u: false });
      const text = out.join('').trim();
      if (text) lines.push(text, '');
      buf = [];
    };
    for (const el of nodes) {
      if (!(el instanceof HTMLElement) || !BLOCK.test(el.tagName)) {
        buf.push(el);
        continue;
      }
      flush();
      const tag = el.tagName;
      if (tag === 'UL' || tag === 'OL') {
        let n = 1;
        for (const li of el.children) {
          const text = inlineOf(li).trim();
          if (text) lines.push(tag === 'UL' ? `- ${text}` : `${n++}. ${text}`);
        }
        lines.push('');
      } else if (/^H[1-6]$/.test(tag)) {
        const text = inlineOf(el).trim();
        if (text) lines.push(`## ${text.replace(/\n/g, ' ')}`, '');
      } else if ([...el.children].some((c) => BLOCK.test(c.tagName))) {
        walk([...el.childNodes]);
      } else {
        const text = inlineOf(el).trim();
        if (text) lines.push(text, '');
      }
    }
    flush();
  };
  walk([...root.childNodes]);
  return lines
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ---- the component -----------------------------------------------------------------------------

export function RichEditor({
  value,
  onChange,
  readOnly = false,
  label,
}: {
  value: string;
  onChange: (md: string) => void;
  readOnly?: boolean;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // What we last put into, or read from, the box, so a save coming back does not move the cursor.
  const synced = useRef<string | null>(null);
  const [length, setLength] = useState(value.length);

  useEffect(() => {
    const el = ref.current;
    if (!el || synced.current === value) return;
    fill(el, value);
    synced.current = value;
    setLength(value.length);
  }, [value]);

  const read = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const md = toMarkers(el);
    setLength(md.length);
    if (md.length > COMMENTS_MAX) return;
    synced.current = md;
    onChange(md);
  }, [onChange]);

  const run = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    read();
  };
  const tools: { label: string; icon: React.ReactNode; act: () => void }[] = [
    { label: 'Bold', icon: <Bold size={15} />, act: () => run('bold') },
    { label: 'Italic', icon: <Italic size={15} />, act: () => run('italic') },
    { label: 'Underline', icon: <Underline size={15} />, act: () => run('underline') },
    { label: 'Heading', icon: <Heading size={15} />, act: () => run('formatBlock', 'h4') },
    { label: 'Bullet list', icon: <List size={15} />, act: () => run('insertUnorderedList') },
    {
      label: 'Numbered list',
      icon: <ListOrdered size={15} />,
      act: () => run('insertOrderedList'),
    },
    {
      label: 'Clear formatting',
      icon: <RemoveFormatting size={15} />,
      act: () => {
        run('removeFormat');
        run('formatBlock', 'p');
      },
    },
  ];

  return (
    <div className="rounded-lg border border-line bg-paper">
      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-1 border-b border-line px-2 py-1.5">
          {tools.map((t) => (
            <button
              key={t.label}
              type="button"
              title={t.label}
              aria-label={t.label}
              // Keep the selection in the text while a button is pressed.
              onMouseDown={(e) => e.preventDefault()}
              onClick={t.act}
              className="grid h-8 w-8 place-items-center rounded-md text-slate hover:bg-brand-soft hover:text-navy"
            >
              {t.icon}
            </button>
          ))}
          <span
            className={clsx(
              'ml-auto text-[12px]',
              length > COMMENTS_MAX ? 'text-flag' : 'text-slate',
            )}
          >
            {length} / {COMMENTS_MAX}
          </span>
        </div>
      )}
      {/* Drag the bottom-right corner to make the box taller or shorter. */}
      {/* biome-ignore lint/a11y/useSemanticElements: formatted text needs an editable element; a textarea cannot show bold or lists */}
      <div
        ref={ref}
        role="textbox"
        tabIndex={0}
        aria-multiline="true"
        aria-label={label}
        aria-readonly={readOnly}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={read}
        onBlur={read}
        onPaste={(e) => {
          // Only the words come in, never another program's styles.
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        className={clsx(
          'min-h-28 max-h-[70vh] resize-y overflow-auto px-3 py-2 text-[14px] leading-relaxed outline-none',
          '[&_h4]:mt-1 [&_h4]:text-[15px] [&_h4]:font-semibold [&_ol]:ml-5 [&_ol]:list-decimal [&_p]:my-1 [&_ul]:ml-5 [&_ul]:list-disc',
          readOnly ? 'bg-canvas/60' : 'focus:ring-3 focus:ring-brand/15',
        )}
      />
    </div>
  );
}
